<template>
  <a-card :bordered="false" class="bank-connect-card">
    <div class="bank-connect-card__content">
      <div class="bank-connect-card__hero">
        <p class="bank-connect-card__eyebrow">Yapily sandbox workspace</p>
        <h2>Connect UK bank accounts through a cleaner, review-ready banking console.</h2>
        <p>
          Launch Yapily Connect, capture consent, and review account balances and the last 90 days of stored transactions in one place.
        </p>

        <div class="bank-connect-card__highlights">
          <span>Sandbox-safe consent flow</span>
          <span>90 day transaction sync</span>
          <span>Stored account history</span>
        </div>
      </div>

      <div class="bank-connect-card__actions">
        <div class="bank-connect-card__control-panel">
          <div class="bank-connect-card__metric-row">
            <div>
              <span>Accounts</span>
              <strong>{{ accountCount }}</strong>
            </div>
            <div>
              <span>Transactions</span>
              <strong>{{ transactionCount }}</strong>
            </div>
          </div>

          <div class="bank-connect-card__selector-block">
            <label class="bank-connect-card__selector-label" for="yapily-institution-select">Institution</label>
            <a-select
              id="yapily-institution-select"
              show-search
              size="large"
              :value="selectedInstitutionId || undefined"
              :options="institutionOptions"
              :loading="loadingInstitutions"
              :disabled="loading || loadingInstitutions || !institutions.length"
              option-filter-prop="label"
              placeholder="Select a bank to connect"
              @change="$emit('update:selectedInstitutionId', $event)"
            />
            <p class="bank-connect-card__selector-help">
              Choose the registered Yapily sandbox institution you want to authorize before starting the consent flow.
            </p>
          </div>

          <div class="bank-connect-card__status-box">
            <a-alert v-if="error" type="error" show-icon :message="error" />
            <a-alert v-else-if="message" type="success" show-icon :message="message" />
            <p v-else>
              Ready to open Yapily Connect and start a fresh sandbox authorization.
            </p>
          </div>

          <a-button
            type="primary"
            size="large"
            class="bank-connect-card__button"
            :loading="loading"
            :disabled="loadingInstitutions || disconnecting || !selectedInstitutionId"
            @click="$emit('connect')"
          >
            Connect With Yapily
          </a-button>

          <a-button
            danger
            size="large"
            class="bank-connect-card__button bank-connect-card__button--secondary"
            :loading="disconnecting"
            :disabled="loading || !canDisconnect"
            @click="$emit('disconnect')"
          >
            Disconnect Yapily
          </a-button>
        </div>
      </div>
    </div>
  </a-card>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BankConnect',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    loadingInstitutions: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    institutions: {
      type: Array,
      default: () => [],
    },
    selectedInstitutionId: {
      type: String,
      default: '',
    },
    accountCount: {
      type: Number,
      default: 0,
    },
    transactionCount: {
      type: Number,
      default: 0,
    },
    disconnecting: {
      type: Boolean,
      default: false,
    },
    canDisconnect: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['connect', 'disconnect', 'update:selectedInstitutionId'],
  computed: {
    institutionOptions() {
      return this.institutions.map((institution) => ({
        value: institution.id,
        label: [
          institution.full_name || institution.name || institution.id,
          institution.recommended ? 'Recommended' : null,
          institution.supports_transactions ? 'Transactions' : null,
        ].filter(Boolean).join(' · '),
      }));
    },
  },
  setup() {

    return {
    };
  },
});
</script>

<style scoped>
.bank-connect-card {
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(45, 212, 191, 0.22), transparent 32%),
    linear-gradient(135deg, #0f172a 0%, #111827 38%, #133b5c 100%);
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
}

.bank-connect-card :deep(.ant-card-body) {
  padding: 34px;
}

.bank-connect-card__content {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 380px);
  gap: 28px;
  align-items: stretch;
  color: #f8fafc;
}

.bank-connect-card__hero {
  display: grid;
  align-content: start;
}

.bank-connect-card__eyebrow {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(226, 232, 240, 0.72);
}

.bank-connect-card h2 {
  color: #ffffff;
  margin-bottom: 12px;
  font-size: 34px;
  line-height: 1.15;
}

.bank-connect-card__hero > p:last-of-type {
  margin-bottom: 0;
  max-width: 660px;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(226, 232, 240, 0.86);
}

.bank-connect-card__highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.bank-connect-card__highlights span {
  padding: 9px 14px;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
}

.bank-connect-card__actions {
  display: flex;
}

.bank-connect-card__control-panel {
  width: 100%;
  display: grid;
  gap: 18px;
  padding: 22px;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.bank-connect-card__metric-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.bank-connect-card__metric-row div {
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.34);
}

.bank-connect-card__metric-row span {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(226, 232, 240, 0.68);
}

.bank-connect-card__metric-row strong {
  font-size: 24px;
  color: #ffffff;
}

.bank-connect-card__status-box {
  min-height: 94px;
  display: grid;
  align-items: center;
}

.bank-connect-card__status-box p {
  margin: 0;
  color: rgba(226, 232, 240, 0.84);
  line-height: 1.6;
}

.bank-connect-card__selector-block {
  display: grid;
  gap: 10px;
}

.bank-connect-card__selector-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(226, 232, 240, 0.72);
}

.bank-connect-card__selector-help {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(226, 232, 240, 0.72);
}

.bank-connect-card__selector-block :deep(.ant-select-selector) {
  min-height: 48px;
  border: 1px solid rgba(226, 232, 240, 0.18) !important;
  border-radius: 14px !important;
  background: rgba(15, 23, 42, 0.38) !important;
  box-shadow: none !important;
}

.bank-connect-card__selector-block :deep(.ant-select-selection-placeholder),
.bank-connect-card__selector-block :deep(.ant-select-selection-item),
.bank-connect-card__selector-block :deep(.ant-select-arrow) {
  color: #f8fafc;
}

.bank-connect-card__status-box :deep(.ant-alert) {
  border: none;
}

.bank-connect-card__button {
  height: 48px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #14b8a6 0%, #0f766e 100%);
  box-shadow: 0 12px 24px rgba(20, 184, 166, 0.24);
}

.bank-connect-card__button--secondary {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

@media only screen and (max-width: 767px) {
  .bank-connect-card__content {
    grid-template-columns: 1fr;
  }

  .bank-connect-card h2 {
    font-size: 28px;
  }
}
</style>